import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { date } from 'assets/utils/data/Date';
import { autoTriggerDownload } from 'assets/utils/dom/Download';
import { generateDataUrl } from 'assets/utils/dom/UrlParsing';
import MimeType from 'assets/utils/file/mimeType/MimeType';
import { HttpStatus } from 'config/Api.Config';
import PricingDistributionTemplate from 'models/productManagement/pricingDistributionTemplate/Model.PricingDistributionTemplate';
import { productPricingExportExcelAsBase64Api } from 'models/productManagement/product/export/Api.Product.Export';
import Distributor from 'models/sales/distributor/Model.Distributor';
import React from 'react';
import { usePriceSelectionTools } from '../hooks/usePriceSelectionTools';

type PriceExportProps = {
  productId: number;
  setIsExporting: React.Dispatch<React.SetStateAction<boolean>>;
};
const PriceExport = ({ productId, setIsExporting }: PriceExportProps) => {
  const { lang } = getLocales();
  const { isDealerOrDistributor } = useAuthStore();
  const priceSelectionTools = usePriceSelectionTools();

  const distributors = priceSelectionTools?.distributors?.map((it) => new Distributor(it));
  const templates = priceSelectionTools?.templates?.map((it) => new PricingDistributionTemplate(it));

  return (
    <ModalForm<Api.Core.Product.Export.IRequest>
      subcomponents={ThemeModalFormDefault}
      dependencyArray={[]}
      title={lang.exportPrices}
      default={{
        productId,
        distributorId: priceSelectionTools.distributor?.id,
        pricingDistributionTemplateId: priceSelectionTools.distributorTemplate?.id,
        includeDistributorPricing: false,
        provinceGroupId: undefined,
      }}
      onCancel={() => setIsExporting(false)}
      onSubmit={async (data) => {
        const res = await productPricingExportExcelAsBase64Api({ ...data });

        if (res?.status === HttpStatus.ok) {
          autoTriggerDownload({
            url: generateDataUrl(MimeType.appOPEN_XML_SHEET, res?.payload),
            name: `P${productId}_${priceSelectionTools?.dataType}_${date().format('DD-MM-YY')}.xlsx`,
          });
          addLog({ success: lang.exportSuccess });
        } else addLog({ error: res.message });

        setIsExporting(false);
      }}
      render={({ form }) => (
        <>
          <FormWrapper>
            <CheckInput
              class={inputStyles.check.switch}
              name="includeDistributorPricing"
              label={lang.includeDistributorPricing}
              value={form.data.includeDistributorPricing}
              onChange={form.update}
            />
            &nbsp;
            {form.data.includeDistributorPricing && (
              <InputWrapper>
                {distributors?.length && !isDealerOrDistributor && (
                  <SelectInput
                    name="distributorId"
                    value={form.data.distributorId}
                    label={lang.distributors}
                    data={distributors?.map((item) => ({
                      ...item.displayInfo,
                      disabled:
                        !!priceSelectionTools.dealer?.id && item?.id !== priceSelectionTools.dealer?.representativeId,
                    }))}
                    onChange={({ distributorId }) => {
                      form.update({ distributorId });
                    }}
                    style={{ width: '200px' }}
                  />
                )}
                <SelectInput
                  name="pricingDistributionTemplateId"
                  value={form.data.pricingDistributionTemplateId}
                  label={lang.pricingDistributionTemplate}
                  data={templates?.map((item) => item.displayInfo)}
                  onChange={({ pricingDistributionTemplateId }) => {
                    form.update({
                      pricingDistributionTemplateId,
                    });
                  }}
                  disabled={!!priceSelectionTools.distributorTemplate?.id}
                />
              </InputWrapper>
            )}
          </FormWrapper>
        </>
      )}
    />
  );
};

export default PriceExport;
